import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import {
  ClientRepresentation,
  GroupRepresentation,
  KeycloakAdminApi,
  RoleRepresentation,
  UserRepresentation,
} from '../../services/keycloak-admin-api';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SidebarService } from '../../services/sidebar.service';

@Component({
  selector: 'app-group-edit',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, FormsModule],
  templateUrl: './group-edit.html',
  styleUrl: './group-edit.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class GroupEdit implements OnInit {
  private keycloakApi = inject(KeycloakAdminApi);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private sidebarService = inject(SidebarService);

  // Input properties for sidebar mode
  @Input() groupIdInput?: string;
  @Input() groupInput?: GroupRepresentation;
  @Input() isSidebarMode = false;

  groupId: string | null = null;
  group: GroupRepresentation | null = null;
  isLoading = false;
  isSaving = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  activeTab = 'details';
  groupForm: FormGroup;

  // For role management
  clients: ClientRepresentation[] = [];
  selectedClient: string | null = null;
  availableRoles: RoleRepresentation[] = [];
  assignedRoles: RoleRepresentation[] = [];
  effectiveRoles: RoleRepresentation[] = [];
  isLoadingRoles = false;

  // For member management
  groupMembers: UserRepresentation[] = [];
  availableUsers: UserRepresentation[] = [];
  isLoadingMembers = false;
  memberSearchTerm = '';
  filteredAvailableUsers: UserRepresentation[] = [];

  // Track loading states for individual operations
  loadingRoleIds = new Set<string>();

  constructor() {
    this.groupForm = this.fb.group({
      name: ['', Validators.required],
      path: [{ value: '', disabled: true }],
    });
  }

  ngOnInit(): void {
    // Use input properties for sidebar mode, otherwise use route params
    if (this.isSidebarMode) {
      this.groupId = this.groupIdInput || null;
      this.group = this.groupInput || null;
      if (this.group) {
        this.groupForm.patchValue({
          name: this.group.name || '',
          path: this.group.path || '',
        });
      }
    } else {
      this.groupId = this.route.snapshot.params['id'];
    }

    if (this.groupId && !this.group) {
      this.loadGroup();
    }

    // Load clients for role management
    this.loadClients();
  }

  setActiveTab(tabEvent: Event): void {
    this.activeTab = (tabEvent as CustomEvent)?.detail?.activeTab;

    if (this.activeTab === 'roles' && this.selectedClient) {
      this.loadClientRoles(this.selectedClient);
    } else if (this.activeTab === 'members') {
      this.loadMembers();
    }
  }

  loadGroup(): void {
    if (!this.groupId) return;

    this.isLoading = true;
    this.keycloakApi
      .getGroupById(this.groupId)
      .then((group) => {
        this.group = group;
        this.groupForm.patchValue({
          name: group.name || '',
          path: group.path || '',
        });
        this.isLoading = false;
      })
      .catch((error) => {
        this.errorMessage = `Failed to load group: ${error}`;
        this.isLoading = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  // Client and role management
  loadClients(): void {
    this.keycloakApi
      .getClients()
      .then((clients) => {
        this.clients = clients;
        if (clients.length > 0 && !this.selectedClient) {
          this.selectedClient = clients[0].id || null;
          if (this.activeTab === 'roles' && this.selectedClient) {
            this.loadClientRoles(this.selectedClient);
          }
        }
      })
      .catch((error) => {
        this.errorMessage = `Failed to load clients: ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  onClientChange(clientId: string): void {
    this.selectedClient = clientId;
    if (this.selectedClient) {
      this.loadClientRoles(this.selectedClient);
    } else {
      this.availableRoles = [];
      this.assignedRoles = [];
    }
  }

  loadClientRoles(clientId: string): void {
    if (!this.groupId) return;

    this.isLoadingRoles = true;

    // Load both available and assigned roles
    Promise.all([
      this.keycloakApi.getAvailableGroupClientRoleMappings(this.groupId, clientId),
      this.keycloakApi.getGroupClientRoleMappings(this.groupId, clientId),
      this.keycloakApi.getEffectiveGroupClientRoleMappings(this.groupId, clientId),
    ])
      .then(([available, assigned, effective]) => {
        this.availableRoles = available;
        this.assignedRoles = assigned;
        this.effectiveRoles = effective;
        this.isLoadingRoles = false;
      })
      .catch((error) => {
        this.errorMessage = `Failed to load roles: ${error}`;
        this.isLoadingRoles = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  assignRole(role: RoleRepresentation): void {
    if (!this.groupId || !this.selectedClient || !role.name || !role.id) return;

    this.loadingRoleIds.add(role.id);
    const roleToAssign: RoleRepresentation = { id: role.id, name: role.name };

    this.keycloakApi
      .addClientRolesToGroup(this.groupId, this.selectedClient, [roleToAssign])
      .then(() => {
        // Move role from available to assigned
        this.availableRoles = this.availableRoles.filter((r) => r.id !== role.id);
        this.assignedRoles.push(role);
        if (role.id != null) {
          this.loadingRoleIds.delete(role.id);
        }
        this.successMessage = `Role "${role.name}" assigned successfully`;
        this.effectiveRoles.push(role);
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to assign role: ${error}`;
        if (role.id != null) {
          this.loadingRoleIds.delete(role.id);
        }
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  removeRole(role: RoleRepresentation): void {
    if (!this.groupId || !this.selectedClient || !role.name || !role.id) return;

    this.loadingRoleIds.add(role.id);
    const roleToRemove: RoleRepresentation = { id: role.id, name: role.name };

    this.keycloakApi
      .removeClientRolesFromGroup(this.groupId, this.selectedClient, [roleToRemove])
      .then(() => {
        // Move role from assigned to available
        this.assignedRoles = this.assignedRoles.filter((r) => r.id !== role.id);
        this.availableRoles.push(role);
        if (role.id != null) {
          this.loadingRoleIds.delete(role.id);
        }
        if (this.effectiveRoles.find((r) => r.id === role.id)) {
          this.keycloakApi
            .getEffectiveGroupClientRoleMappings(this.groupId!, this.selectedClient!)
            .then((effective) => {
              this.effectiveRoles = effective;
            });
        }
        this.successMessage = `Role "${role.name}" removed successfully`;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to remove role: ${error}`;
        if (role.id != null) {
          this.loadingRoleIds.delete(role.id);
        }
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  // Member management
  loadMembers(): void {
    if (!this.groupId) return;

    this.isLoadingMembers = true;

    // Load both group members and all users for search
    Promise.all([
      this.keycloakApi.getGroupMembers(this.groupId),
      this.keycloakApi.getUsers(undefined, 0, 100),
    ])
      .then(([members, allUsers]) => {
        this.groupMembers = members;
        this.availableUsers = allUsers;
        this.filterAvailableUsers();
        this.isLoadingMembers = false;
      })
      .catch((error) => {
        this.errorMessage = `Failed to load members: ${error}`;
        this.isLoadingMembers = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  filterAvailableUsers(): void {
    const memberIds = this.groupMembers.map((member) => member.id);

    let filtered = this.availableUsers.filter((user) => !memberIds.includes(user.id));

    if (this.memberSearchTerm) {
      const term = this.memberSearchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.username?.toLowerCase().includes(term) ||
          user.email?.toLowerCase().includes(term) ||
          user.firstName?.toLowerCase().includes(term) ||
          user.lastName?.toLowerCase().includes(term)
      );
    }

    this.filteredAvailableUsers = filtered;
  }

  onMemberSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.memberSearchTerm = input.value;
    this.filterAvailableUsers();
  }

  addMember(user: UserRepresentation): void {
    if (!this.groupId || !user.id) return;

    this.isSaving = true;
    this.keycloakApi
      .addUserToGroup(user.id, this.groupId)
      .then(() => {
        this.groupMembers.push(user);
        this.filterAvailableUsers();
        this.isSaving = false;
        this.successMessage = `User "${this.formatDisplayName(user)}" added successfully`;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to add user to group: ${error}`;
        this.isSaving = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  removeMember(user: UserRepresentation): void {
    if (!this.groupId || !user.id) return;

    this.isSaving = true;
    this.keycloakApi
      .removeUserFromGroup(user.id, this.groupId)
      .then(() => {
        this.groupMembers = this.groupMembers.filter((m) => m.id !== user.id);
        this.filterAvailableUsers();
        this.isSaving = false;
        this.successMessage = `User "${this.formatDisplayName(user)}" removed successfully`;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to remove user from group: ${error}`;
        this.isSaving = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  // Utility methods
  formatDisplayName(user: UserRepresentation): string {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.firstName) {
      return user.firstName;
    } else if (user.lastName) {
      return user.lastName;
    } else {
      return user.username || 'Unknown';
    }
  }

  // Close sidebar
  closeSidebar(): void {
    this.sidebarService.closeSidebar();
  }
}
